import { useCallback, useEffect, useState } from 'react'
import { GetStaticPaths } from 'next'
import { InView } from 'react-intersection-observer'
import { LinkButton } from '@/atoms/Button'
import { GlassCircle } from '@/atoms/GlassCircle'
import { CircleChevronDownIcon } from '@/atoms/Icons/CircleChevronDownIcon'
import { EarlyAccessIcon } from '@/atoms/Icons/EarlyAccessIcon'
import { GiftIcon } from '@/atoms/Icons/GiftIcon'
import { GuildSolidIcon } from '@/atoms/Icons/GuildSolidIcon'
import { LivestreamIcon } from '@/atoms/Icons/LivestreamIcon'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsDiv, HeadingLG, LabelLG, ParagraphMD, TitleMD } from '@/atoms/Text'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { Seo } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { BitAngelPlayer } from '@/organisms/BitAngelPlayer'
import { PifVideoPlayerUI } from '@/organisms/BitAngelPlayer/PifVideoPlayerUI'
import { Faq } from '@/services/FaqService'
import { getGuildPromotion, Promotion } from '@/services/GuildPromotionService'
import { useSafeTrack } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { writeToSessionStorage } from '@/utils/session-storage'
import { getBasePath } from '@/utils/sitemap/base'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { GuildPlanSelectionTiers } from '@/views/GuildJoinView/GuildPlanSelectionTiers'
import { GuildSignupContextProvider } from '@/views/GuildSignupView/GuildSignupContext'
import { getGuildApprovedTorchPosters } from '@/views/GuildSignupView/GuildSignupContext/GuildSignupContext'

interface TheShiftExclusiveSceneProps {
  guildApprovedTorchPosters: string[]
  faqs: Faq[]
  promotion: Promotion | null
  locale: string
}

export const TheShiftExclusiveScene: NextPageWithLayout<TheShiftExclusiveSceneProps> = ({
  faqs,
  guildApprovedTorchPosters,
  promotion,
  locale,
}) => {
  const track = useSafeTrack()
  const { trackProductViewed } = useEcommerceEvents()
  const { t } = useTranslate('the-shift-exclusive')
  const [isPlayerLoaded, setPlayerLoaded] = useState<boolean>(false)
  const baseUrl = getBasePath(locale)

  useEffect(() => {
    writeToSessionStorage('pageSlug', 'the-shift-exclusive-scene')
    writeToSessionStorage('projectSlugs', slugs.theShift)
  }, [])

  const trackVideoStart = useCallback(() => {
    track('Video Content Started', {
      position: 0,
      program: slugs.theShift,
      publisher: 'angel-studios',
      title: 'Exclusive Bonus Scene',
      video_type: 'video',
    })
  }, [track])

  const trackVideoCompleted = useCallback(() => {
    track('Video Content Completed', {
      position: 0,
      program: slugs.theShift,
      publisher: 'angel-studios',
      title: 'Exclusive Bonus Scene',
      video_type: 'video',
    })
  }, [track])

  return (
    <div>
      <Seo
        nofollow
        noindex
        description={t(
          'theShiftExclusiveGiftDescription',
          'Watch this never before seen exclusive bonus scene of The Shift and take advantage of our best deal yet!',
        )}
        openGraph={{}}
        title={t('theShiftExclusiveGift', 'The Shift Exclusive Gift')}
        canonical={`${baseUrl}/m/the-shift-exclusive-scene`}
      />
      <ResponsiveBackgroundImage
        src="/v1689354516/angel-app/assets/discover/background_blur_v2.webp"
        preBackgroundStyles="linear-gradient(180deg, rgba(21, 21, 21, 0.3) 50%, #151515 100%), "
        className="pointer-events-none h-[75vh] touch-none select-none sm:h-screen"
      />
      <GuildSignupContextProvider
        guildFaqs={faqs}
        guildApprovedTorchPosters={guildApprovedTorchPosters}
        promotion={promotion}
      >
        <div className="relative grid grid-cols-12 px-4 py-12 lg:py-48">
          <Image
            src="https://images.angelstudios.com/image/upload/v1697754786/studio-app/catalog/754b338d-2a3d-40c0-a822-690fdfb936e1.webp"
            alt={t('theShift', 'The Shift')}
            height={240}
            width={240}
            priority
            className="order-0 col-span-12 mx-auto h-[120px] w-auto sm:col-span-10 sm:col-start-2 sm:h-[180px] lg:col-span-4 lg:col-start-2"
          />

          <div className="order-1 col-span-12 mb-8 md:order-2 md:col-span-10 md:col-start-2 lg:col-span-5 lg:col-start-7 lg:row-span-2 lg:row-start-1 lg:self-center">
            {isPlayerLoaded && (
              <BitAngelPlayer
                autoplay
                className="aspect-h-9 aspect-w-16 relative z-10 overflow-hidden rounded-2xl"
                ui={PifVideoPlayerUI}
                url="https://media.angelstudios.com/copied-content/masters/TheShift_Leng_20_BonusPifScene_4k_Flat-lowres.mp4"
                onStart={trackVideoStart}
                onPlaybackFinished={trackVideoCompleted}
              />
            )}
            {!isPlayerLoaded && (
              <div className="relative cursor-pointer" onClick={() => setPlayerLoaded(true)}>
                <div className="aspect-h-9 aspect-w-16 relative overflow-hidden rounded-2xl">
                  <Image
                    src="https://images.angelstudios.com/image/upload/v1703045777/angel-studios/guild/bonus-scene.webp"
                    alt="The Shift Bonus Scene"
                    fill
                    priority
                  />
                </div>
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <GlassCircle size={60}>
                    <PlayIcon size={30} />
                  </GlassCircle>
                </div>
              </div>
            )}
          </div>

          <div className="order-3 col-span-12 mb-8 text-center sm:col-span-8 sm:col-start-3 lg:col-span-4 lg:col-start-2">
            <ParagraphMD
              as={AsDiv}
              weight="normal"
              color="white"
              className="sm:photon-paragraph-xl mx-auto mb-6 max-w-[34ch] sm:max-w-[35ch]"
            >
              <Translate i18nKey="enjoyThisExclusiveBonusSceneThenLearnMoreV2" t={t}>
                Enjoy this <b>exclusive bonus scene</b> from <i>The Shift</i> as our way of saying thank you. Become a
                Guild member and help us make more movies that matter!
              </Translate>
            </ParagraphMD>
            <LinkButton href="#plans" variant="white" className="mx-auto w-fit px-10 py-3">
              <GuildSolidIcon color1="core-gray-950" color2="white" size={24} className="mr-1 cursor-pointer" />
              <LabelLG weight="semibold" color="core-gray-950">
                <Translate i18nKey="joinTheGuild" t={t}>
                  Join the Guild
                </Translate>
              </LabelLG>
            </LinkButton>
          </div>

          <div className="order-4 col-span-12 mb-16 mt-8 text-center sm:col-span-10 sm:col-start-2 lg:col-span-6 lg:col-start-4 lg:mt-48">
            <InternalLink href="#guild">
              <LabelLG weight="bold" color="white" className="mb-4">
                <Translate i18nKey="learnMoreAboutTheGuild" t={t}>
                  Learn more about the Guild.
                </Translate>
              </LabelLG>

              <CircleChevronDownIcon className="mx-auto" />
            </InternalLink>
          </div>
        </div>

        <div id="guild" className="order-5 col-span-12 mb-12 grid w-full scroll-mt-20 grid-cols-12 px-4">
          <div className="col-span-12 mb-12 text-center md:col-span-10 md:col-start-2 lg:col-span-6 lg:col-start-4">
            <HeadingLG weight="bold" color="white" className="mb-8">
              <Translate i18nKey="introducingTheAngelGuild" t={t}>
                Introducing the Angel Guild
              </Translate>
            </HeadingLG>
            <ParagraphMD weight="medium" color="white">
              <Translate i18nKey="memberGetBenefits" t={t}>
                Members of the Angel Guild are the reasons quality movies like <i>The Shift</i> are possible. Guild
                members receive premium benefits.
              </Translate>
            </ParagraphMD>
          </div>

          <div className="col-span-12 mb-32 grid grid-cols-1 gap-8 text-left text-white md:col-span-10 md:col-start-3 md:col-end-11 md:grid-cols-2">
            <div className="rounded-2xl bg-core-gray-900 p-6">
              <div>
                <TicketIcon size={32} className="mb-4" />
                <TitleMD weight="bold" className="mb-2">
                  <Translate i18nKey="freeMovieTicketsV2" t={t}>
                    Movie Tickets
                  </Translate>
                </TitleMD>
                <ParagraphMD weight="medium" color="core-gray-400">
                  <Translate i18nKey="youMadeItHappenGetTicketsv3" t={t}>
                    You made it happen, you get to see it on the big screen. Guild members get 2 tickets to each Angel
                    theatrical release.
                  </Translate>
                </ParagraphMD>
              </div>
            </div>

            <div className="rounded-2xl bg-core-gray-900 p-6">
              <div>
                <EarlyAccessIcon color="cool-02" size={32} className="mb-4" />
                <TitleMD weight="bold" className="mb-2">
                  <Translate i18nKey="earlyAccess" t={t}>
                    Early Access
                  </Translate>
                </TitleMD>

                <ParagraphMD as={AsDiv} weight="medium" color="core-gray-400" className="mb-8">
                  <Translate i18nKey="streamYourFavoritesEarlyAngel" t={t}>
                    Stream EVERY Angel movie and show from the comfort of your home before anyone else.
                  </Translate>
                </ParagraphMD>
              </div>
            </div>

            <div className="rounded-2xl bg-core-gray-900 p-6">
              <div>
                <LivestreamIcon size={32} className="mb-4" color="error-500" />
                <TitleMD weight="bold" className="mb-2">
                  <Translate i18nKey="guildOnlyLivestreams" t={t}>
                    Guild Only Livestreams
                  </Translate>
                </TitleMD>
                <ParagraphMD weight="medium" color="core-gray-400" className="mb-8">
                  <Translate i18nKey="tuneIntoLivestreamsWithFilmmakers" t={t}>
                    Tune into livestreams with filmmakers you love and get the inside scoop on your favorite shows.
                  </Translate>
                </ParagraphMD>
              </div>
            </div>

            <div className="rounded-2xl bg-core-gray-900 p-6">
              <div>
                <GiftIcon size={32} className="mb-4" color="warm-02" />
                <TitleMD weight="bold" className="mb-2">
                  <Translate i18nKey="merchDiscounts" t={t}>
                    Merch Discounts
                  </Translate>
                </TitleMD>
                <ParagraphMD weight="medium" color="core-gray-400" className="mb-8">
                  <Translate i18nKey="repYourFavoriteFandom" t={t}>
                    Rep your favorite fandom without breaking the bank with a cool 20% discount in the Angel Shop.
                  </Translate>
                </ParagraphMD>
              </div>
            </div>
          </div>

          <InView
            id="plans"
            className="col-span-12"
            triggerOnce
            onChange={(inView) => {
              if (!inView) return

              trackProductViewed({
                category: 'guild',
                funnel: 'guild',
                name: 'Angel Guild Plans',
                price: 0,
                currency: 'USD',
                quantity: 1,
                product_id: 'Angel Guild Plans',
                sku: 'Angel Guild Plans',
                projectSlug: slugs.theShift,
              })
            }}
          >
            <GuildPlanSelectionTiers />
          </InView>
        </div>
        <LinkButton
          href={`${paths.guild.index}?utm_campaign=the-shift&utm_source=the-shift-exclusive-scene&utm_medium=web`}
          variant="white"
          className="mx-auto mb-80 w-fit px-10 py-3"
        >
          <Translate i18nKey="learnMore" t={t}>
            Learn More
          </Translate>
        </LinkButton>
      </GuildSignupContextProvider>
    </div>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [{ params: { locale: 'en', project: slugs.theShift } }],
    fallback: 'blocking',
  }
}

export const getStaticProps = getStaticPropsErrorHandler<TheShiftExclusiveSceneProps>(
  { filePath: 'pages/[locale]/m/the-shift-exclusive-scene/index.tsx' },
  async ({ federationClient, locale, preview }) => {
    const [guildApprovedTorchPosters, promotion, translations] = await Promise.all([
      getGuildApprovedTorchPosters(),
      getGuildPromotion({
        opts: { locale, preview },
        isSupportLink: true,
        federationClient,
      }),
      loadTranslations(locale, ['common', 'the-shift-exclusive', 'guild', 'join-the-guild']),
    ])

    return {
      props: {
        faqs: [],
        promotion,
        guildApprovedTorchPosters,
        locale,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

TheShiftExclusiveScene.getLayout = (page) => {
  return (
    <DefaultLayout isDarkMode hideNavigation>
      {page}
    </DefaultLayout>
  )
}

export default TheShiftExclusiveScene
